import styled from "@emotion/styled";
import { tokens } from "@/shared/design-system/tokens";
import type { DatePickerAppearance } from "./DatePicker.types";

export const DatePickerRoot = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  min-width: 120px;
`;

export const DatePickerTrigger = styled.button`
  min-height: 40px;
  padding: ${tokens.spacing.sm} ${tokens.spacing.mmd};
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: ${tokens.spacing.mmd};
  border: 1px solid ${tokens.colors.basics[700]};
  border-radius: ${tokens.borderRadius.lg};
  background: ${tokens.colors.blue[500]};
  color: ${tokens.colors.basics[0]};
  font: ${tokens.typography.fontWeight.semibold} ${tokens.typography.fontSize.sm}/1.4
    ${tokens.typography.fontFamily.primary};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 1px ${tokens.colors.basics[700]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const TriggerChevron = styled.span<{
  $open: boolean;
  $appearance: DatePickerAppearance;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $appearance }) =>
    $appearance === "dark"
      ? tokens.colors.basics[0]
      : tokens.colors.blue[800]};
  transition: ${tokens.transitions.fast};
  transform: ${({ $open }) => ($open ? "rotate(270deg)" : "rotate(90deg)")};
`;

export const DatePickerPanel = styled.div`
  box-sizing: border-box;
  position: absolute;
  top: calc(100% + ${tokens.spacing.xs});
  left: 0;
  z-index: ${tokens.zIndex.popover};
  width: min(310px, calc(100vw - 32px));
  max-height: calc(100vh - 112px);
  padding: ${tokens.spacing.mmd};
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  border: 1px solid ${tokens.colors.basics[800]};
  border-radius: ${tokens.borderRadius.xl};
  background: linear-gradient(
    180deg,
    rgba(33, 77, 97, 0.99) 0%,
    rgba(31, 112, 143, 0.99) 100%
  );
  box-shadow: 0 14px 32px rgba(7, 38, 49, 0.42);
  color: ${tokens.colors.basics[0]};
`;

export const YearNavigation = styled.div`
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 28px;
  align-items: center;
  gap: ${tokens.spacing.xs};
  padding-bottom: ${tokens.spacing.sm};
  border-bottom: 1px solid ${tokens.colors.basics[700]};
`;

export const YearOptions = styled.div<{ $single: boolean }>`
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(
    ${({ $single }) => ($single ? 1 : 3)},
    minmax(0, 1fr)
  );
  gap: ${tokens.spacing.xs};
`;

export const YearButton = styled.button`
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: ${tokens.borderRadius.md};
  background: transparent;
  color: ${tokens.colors.basics[0]};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${tokens.colors.blue[800]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 1px ${tokens.colors.basics[700]};
  }

  &:disabled {
    visibility: hidden;
  }
`;

export const YearArrowIcon = styled.span<{
  $direction: "left" | "right";
}>`
  width: 8px;
  height: 8px;
  border-bottom: 1.5px solid currentColor;
  border-left: 1.5px solid currentColor;
  transform: ${({ $direction }) =>
    $direction === "left" ? "rotate(45deg)" : "rotate(225deg)"};
`;

export const YearOption = styled.button<{ $selected: boolean }>`
  min-width: 0;
  min-height: 30px;
  padding: ${tokens.spacing.xs} ${tokens.spacing.sm};
  border: 0;
  border-radius: ${tokens.borderRadius.full};
  background: ${({ $selected }) =>
    $selected ? tokens.colors.basics[100] : "transparent"};
  color: ${({ $selected }) =>
    $selected ? tokens.colors.blue[900] : tokens.colors.basics[0]};
  font: ${tokens.typography.fontWeight.medium} ${tokens.typography.fontSize.xs}/1
    ${tokens.typography.fontFamily.primary};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ $selected }) =>
      $selected ? tokens.colors.basics[100] : tokens.colors.basics[800]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 1px ${tokens.colors.basics[700]};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

export const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: ${tokens.spacing.xs};
  row-gap: 2px;
  padding: ${tokens.spacing.mmd} 0;
`;

export const MonthButton = styled.button<{ $selected: boolean }>`
  min-height: 30px;
  border: 0;
  border-radius: ${tokens.borderRadius.full};
  background: ${({ $selected }) =>
    $selected ? tokens.colors.basics[100] : "transparent"};
  color: ${({ $selected }) =>
    $selected ? tokens.colors.blue[900] : tokens.colors.basics[0]};
  font: ${tokens.typography.fontWeight.normal} ${tokens.typography.fontSize.xs}/1
    ${tokens.typography.fontFamily.primary};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ $selected }) =>
      $selected ? tokens.colors.basics[100] : tokens.colors.basics[800]};
    color: ${({ $selected }) =>
      $selected ? tokens.colors.blue[900] : tokens.colors.basics[0]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 1px ${tokens.colors.basics[700]};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export const ModeSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${tokens.spacing.xs};
  padding: ${tokens.spacing.mmd} 0 ${tokens.spacing.sm};
  border-top: 1px solid ${tokens.colors.basics[700]};
`;

export const ModeButton = styled.button<{ $selected: boolean }>`
  min-height: 32px;
  padding: ${tokens.spacing.xs} ${tokens.spacing.sm};
  border: 0;
  border-radius: ${tokens.borderRadius.full};
  background: ${({ $selected }) =>
    $selected ? tokens.colors.basics[100] : "transparent"};
  color: ${({ $selected }) =>
    $selected ? tokens.colors.blue[900] : tokens.colors.basics[0]};
  font: ${tokens.typography.fontWeight.semibold} ${tokens.typography.fontSize.xs}/1.4
    ${tokens.typography.fontFamily.primary};
  cursor: pointer;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? tokens.colors.basics[100] : tokens.colors.basics[800]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 1px ${tokens.colors.basics[700]};
  }
`;

export const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  column-gap: 2px;
  row-gap: 0;
`;

export const Weekday = styled.span`
  min-height: 26px;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: ${tokens.typography.fontWeight.semibold};
`;

export const DayButton = styled.button<{
  $selected: boolean;
  $outside: boolean;
}>`
  box-sizing: border-box;
  width: 30px;
  height: 30px;
  justify-self: center;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: ${tokens.borderRadius.full};
  background: ${({ $selected }) =>
    $selected ? tokens.colors.blue[500] : "transparent"};
  color: ${tokens.colors.basics[0]};
  opacity: ${({ $outside }) => ($outside ? 0.35 : 1)};
  font: ${tokens.typography.fontWeight.normal} ${tokens.typography.fontSize.xs}/1
    ${tokens.typography.fontFamily.primary};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${tokens.colors.basics[800]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 1px ${tokens.colors.basics[700]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.25;
  }
`;

export const ScreenReaderText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
